import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Task, db, sql } from '../../lib/db';

interface RegularTaskListLogicOptions {
  templates: Task[];
  todaysTasks: Task[];
  overdueTasks: Task[];
  todaysProgress: { total: number; completed: number };
  dbUserId: string;
  onTaskUpdate: (id: string, updates: Partial<Task>) => Promise<void>;
  onTaskDelete: (id: string) => Promise<void>;
}

interface CompletedTaskWithTime {
  title: string;
  description?: string;
  timeSpent: number;
}

/**
 * The regular task list has a lot of moving parts (timers, history, broken-task checks).
 * This hook bundles that behaviour so the UI file stays under the 500 line goal and is easier to read.
 */
export function useRegularTaskListLogic({
  templates,
  todaysTasks,
  overdueTasks,
  todaysProgress,
  dbUserId,
  onTaskUpdate,
  onTaskDelete
}: RegularTaskListLogicOptions) {
  const navigate = useNavigate();

  const [timeEntryTask, setTimeEntryTask] = useState<Task | null>(null);
  const [showTimeEntry, setShowTimeEntry] = useState(false);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
  const [historyTask, setHistoryTask] = useState<{
    templateId: string;
    title: string;
    description?: string;
    frequency?: string;
    priority?: string;
  } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [taskEstimates, setTaskEstimates] = useState<Map<string, number>>(new Map());
  const [hasBrokenTasks, setHasBrokenTasks] = useState(false);
  const [checkingBrokenTasks, setCheckingBrokenTasks] = useState(true);
  const [showCompletedDropdown, setShowCompletedDropdown] = useState(false);
  const [completedTasksWithTime, setCompletedTasksWithTime] = useState<CompletedTaskWithTime[]>([]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const normalizeStartOfDay = (date: Date) => {
    const clone = new Date(date);
    clone.setHours(0, 0, 0, 0);
    return clone;
  };

  const advanceToNextOccurrence = (current: Date, task: Task) => {
    const frequency = (task.frequency || 'Daily').trim();
    const next = new Date(current);

    switch (frequency) {
      case 'Weekdays': {
        do {
          next.setDate(next.getDate() + 1);
        } while (next.getDay() === 0 || next.getDay() === 6);
        break;
      }
      case 'Weekly': {
        const recurrenceDay = typeof task.recurrence_day === 'number' ? task.recurrence_day : null;
        const currentDay = next.getDay();
        let offset = recurrenceDay !== null ? (recurrenceDay - currentDay + 7) % 7 : 7;
        if (offset === 0) {
          offset = 7;
        }
        next.setDate(next.getDate() + offset);
        break;
      }
      case 'Monthly':
        next.setMonth(next.getMonth() + 1);
        break;
      case 'Quarterly':
        next.setMonth(next.getMonth() + 3);
        break;
      case 'Annual':
        next.setFullYear(next.getFullYear() + 1);
        break;
      default:
        next.setDate(next.getDate() + 1);
        break;
    }

    return normalizeStartOfDay(next);
  };

  const computeFutureOccurrence = (baseValue: Date | string | null | undefined, task: Task): Date | null => {
    if (!baseValue) return null;
    let parsed: Date;
    if (typeof baseValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(baseValue)) {
      const [y, m, d] = baseValue.split('-').map(n => parseInt(n, 10));
      parsed = new Date(y, (m - 1), d, 0, 0, 0, 0);
    } else {
      parsed = new Date(baseValue);
    }
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    let candidate = normalizeStartOfDay(parsed);

    if ((task.frequency || 'Daily') === 'Weekdays' && (candidate.getDay() === 0 || candidate.getDay() === 6)) {
      candidate = advanceToNextOccurrence(candidate, task);
    }

    let safety = 0;
    while (candidate < today && safety < 120) {
      candidate = advanceToNextOccurrence(candidate, task);
      safety += 1;
    }

    return candidate;
  };

  useEffect(() => {
    if (!dbUserId) return;
    loadTaskEstimates().catch(error => {
      console.error('Error loading task estimates:', error);
    });
  }, [dbUserId, todaysTasks, overdueTasks]);

  useEffect(() => {
    checkForBrokenTasks().catch(error => {
      console.error('Error checking for broken tasks:', error);
    });
  }, [dbUserId]);

  useEffect(() => {
    if (todaysProgress.completed > 0) {
      loadCompletedTasksWithTime().catch(error => {
        console.error('Error loading completed tasks with time:', error);
      });
    }
  }, [todaysProgress.completed, dbUserId]);

  async function loadTaskEstimates() {
    if (!todaysTasks.length && !overdueTasks.length) {
      setTaskEstimates(new Map());
      return;
    }

    const taskIdentifiers = [...todaysTasks, ...overdueTasks].map(task => ({
      title: task.title,
      description: task.description || '',
    }));

    const estimates = await db.taskCompletionStatistics.getBatchEstimates(taskIdentifiers, dbUserId);
    setTaskEstimates(estimates);
  }

  async function checkForBrokenTasks() {
    if (!dbUserId) return;

    setCheckingBrokenTasks(true);
    try {
      const now = new Date();
      const todayMidnight = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        0, 0, 0, 0
      ));

      const allTasks = await db.tasks.findByUserId(dbUserId);
      const activeRegularTemplates = allTasks.filter(task => task.is_regular && task.status !== 'Complete');
      const activeSignatures = new Set(
        activeRegularTemplates.map(task => `${task.title}|${task.description || ''}|${task.frequency || 'Daily'}`)
      );

      const completedRegularTasks = allTasks.filter(task => {
        if (!task.is_regular || task.status !== 'Complete' || !task.end_date) {
          return false;
        }

        const signature = `${task.title}|${task.description || ''}|${task.frequency || 'Daily'}`;
        if (!activeSignatures.has(signature)) {
          return false;
        }

        const completionDate = new Date(task.end_date);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return completionDate >= sevenDaysAgo && completionDate < todayMidnight;
      });

      const brokenSignatures: string[] = [];

      for (const task of completedRegularTasks) {
        const completionDate = normalizeStartOfDay(new Date(task.end_date));
        const expectedNext = advanceToNextOccurrence(completionDate, task);

        if (expectedNext >= todayMidnight) {
          continue;
        }

        const signature = `${task.title}|${task.description || ''}|${task.frequency || 'Daily'}`;
        const hasActiveSuccessor = activeRegularTemplates.some(active => {
          const activeSignature = `${active.title}|${active.description || ''}|${active.frequency || 'Daily'}`;
          return activeSignature === signature;
        });

        if (!hasActiveSuccessor && !brokenSignatures.includes(signature)) {
          brokenSignatures.push(signature);
        }
      }

      setHasBrokenTasks(brokenSignatures.length > 0);
    } finally {
      setCheckingBrokenTasks(false);
    }
  }

  async function loadCompletedTasksWithTime() {
    if (!dbUserId) return;

    const todayStart = new Date(today);
    const todayEnd = new Date(today);
    todayEnd.setDate(todayEnd.getDate() + 1);

    const completedTasks = await sql<{
      title: string;
      description: string | null;
      total_time_minutes: number;
    }[]>`
      SELECT
        t.title,
        t.description,
        COALESCE(
          SUM(
            CASE
              WHEN te.end_time IS NOT NULL
              THEN EXTRACT(EPOCH FROM (te.end_time - te.start_time)) / 60
              ELSE 0
            END
          ), 0
        ) as total_time_minutes
      FROM regular_task_history rth
      JOIN tasks t ON rth.generated_task_id = t.id
      LEFT JOIN time_entries te ON te.task_id = t.id
        AND te.created_at >= ${todayStart}
        AND te.created_at <= ${todayEnd}
      WHERE rth.user_id = ${dbUserId}
        AND rth.completion_time >= ${todayStart}
        AND rth.completion_time <= ${todayEnd}
      GROUP BY t.title, t.description
      ORDER BY t.title, t.description
    `;

    setCompletedTasksWithTime(
      completedTasks.map(task => ({
        title: task.title,
        description: task.description || undefined,
        timeSpent: Number(task.total_time_minutes) || 0
      }))
    );
  }

  const completeTask = async (taskId: string) => {
    const task = todaysTasks.find(t => t.id === taskId) || overdueTasks.find(t => t.id === taskId);
    if (!task) return;

    const now = new Date();
    await onTaskUpdate(taskId, {
      status: 'Complete',
      end_date: now
    });

    await db.regularTaskHistory.recordCompletion(taskId, dbUserId);

    setCompletedTaskIds(prev => new Set(prev).add(taskId));
    setTimeout(() => {
      setCompletedTaskIds(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      void checkForBrokenTasks();
    }, 1000);

    toast.success('Task marked as complete!');
  };

  const resetTask = async (taskId: string) => {
    await onTaskUpdate(taskId, {
      status: 'To Do',
      start_date: undefined,
      end_date: undefined
    });
    toast.success('Task reset');
  };

  const reactivateTask = async (task: Task) => {
    try {
      const cleanedNotes = task.notes ? task.notes.replace(/\n?\[Deferred to Tomorrow\]/g, '') : '';
      await onTaskUpdate(task.id, {
        is_live: true,
        notes: cleanedNotes || undefined
      });
      toast.success('Task reactivated');
    } catch (error) {
      console.error('Error reactivating task:', error);
      toast.error('Failed to reactivate task');
    }
  };

  const startTimeEntry = (task: Task) => {
    navigate('/focus', { state: { loadTaskId: task.id, source: 'regular-tasks' } });
  };

  const showTaskHistory = async (task: Task) => {
    const template = templates.find(t =>
      t.title === task.title && t.description === task.description && t.frequency === task.frequency
    );

    if (template) {
      setHistoryTask({
        templateId: template.id,
        title: task.title,
        description: task.description,
        frequency: task.frequency,
        priority: task.priority
      });
      setShowHistory(true);
      return;
    }

    if (templates.some(t => t.id === task.id)) {
      setHistoryTask({
        templateId: task.id,
        title: task.title,
        description: task.description,
        frequency: task.frequency,
        priority: task.priority
      });
      setShowHistory(true);
    } else {
      toast.error('Could not find task template');
    }
  };

  const getCompletionRate = () => {
    if (todaysProgress.total === 0) return 0;
    return Math.round((todaysProgress.completed / todaysProgress.total) * 100);
  };

  const getNextRegenerationDate = (task: Task) => {
    if (task.status === 'Complete' && task.end_date) {
      const nextFromCompletion = computeFutureOccurrence(task.end_date, task);
      if (nextFromCompletion) {
        return nextFromCompletion;
      }
    }

    const nextFromDue = computeFutureOccurrence(task.next_due_date, task);
    if (nextFromDue) {
      return nextFromDue;
    }

    return computeFutureOccurrence(task.start_date, task);
  };

  const formatLastCompletion = (date: Date | null | undefined) => {
    if (!date) return 'Never';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObj.getTime())) return 'Never';

    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;

    return format(dateObj, 'MMM d');
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleTimeEntrySaved = async (wasCompleted: boolean) => {
    if (!timeEntryTask) return;

    if (wasCompleted) {
      await db.regularTaskHistory.recordCompletion(timeEntryTask.id, dbUserId);

      setCompletedTaskIds(prev => new Set(prev).add(timeEntryTask.id));
      setTimeout(() => {
        setCompletedTaskIds(prev => {
          const next = new Set(prev);
          next.delete(timeEntryTask.id);
          return next;
        });
      }, 1000);

      const totalMinutes = await db.timeEntries.getTotalMinutesForTask(timeEntryTask.id);
      await onTaskUpdate(timeEntryTask.id, {
        status: 'Complete',
        end_date: new Date(),
        time_spent: totalMinutes,
        time_spent_minutes: totalMinutes,
        _timerCompletion: true
      });
    } else {
      await onTaskUpdate(timeEntryTask.id, {
        _timerUpdate: true
      });
    }
  };

  return {
    today,
    timeEntryTask,
    setTimeEntryTask,
    showTimeEntry,
    setShowTimeEntry,
    completedTaskIds,
    setCompletedTaskIds,
    historyTask,
    setHistoryTask,
    showHistory,
    setShowHistory,
    taskEstimates,
    hasBrokenTasks,
    checkingBrokenTasks,
    showCompletedDropdown,
    setShowCompletedDropdown,
    completedTasksWithTime,
    completeTask,
    resetTask,
    reactivateTask,
    startTimeEntry,
    showTaskHistory,
    getCompletionRate,
    getNextRegenerationDate,
    formatLastCompletion,
    formatDuration,
    handleTimeEntrySaved,
    loadCompletedTasksWithTime,
    loadTaskEstimates
  };
}
